import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { CiSearch } from "react-icons/ci";
import {
  getAllPendingWhiteLabels,
  approveWhiteLabel,
  rejectWhiteLabel,
} from "@/services/whiteLabel.service";
import { WhiteLabel } from "@/models/whiteLabel.model";
import WhiteLabelForm from "@/components/WhiteLabelForm";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { MdColorLens } from "react-icons/md";
import { ChevronRightIcon, ChevronLeftIcon } from "@chakra-ui/icons";

export default function PendingWhiteLabelsPage() {
  const [whiteLabels, setWhiteLabels] = useState<WhiteLabel[]>([]);
  const [filteredWhiteLabels, setFilteredWhiteLabels] = useState<WhiteLabel[]>(
    []
  );
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [selectedWhiteLabel, setSelectedWhiteLabel] =
    useState<WhiteLabel | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const backgroundStyle = useConditionalBackground();

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.toLowerCase();
    setSearch(query);
    setFilteredWhiteLabels(
      whiteLabels.filter(
        (whiteLabel) =>
          whiteLabel.name.toLowerCase().includes(query) ||
          whiteLabel.slug.toLowerCase().includes(query)
      )
    );
  };

  const fetchPendingWhiteLabels = async () => {
    setIsLoading(true);
    try {
      const { data, totalPages } = await getAllPendingWhiteLabels(
        page,
        pageSize,
        "desc"
      );
      setWhiteLabels(data);
      setFilteredWhiteLabels(data);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Error fetching pending WhiteLabels:", error);
      toast({
        title: "Erro ao carregar WhiteLabels pendentes",
        description: "Tente novamente mais tarde.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await approveWhiteLabel(id);
      toast({
        title: "WhiteLabel aprovado com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchPendingWhiteLabels();
      onClose();
    } catch (error) {
      console.error("Error approving WhiteLabel:", error);
      toast({
        title: "Erro ao aprovar WhiteLabel",
        description: "Tente novamente mais tarde.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };
  const handleReject = async (id: string) => {
    try {
      await rejectWhiteLabel(id);
      toast({
        title: "WhiteLabel rejeitado com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchPendingWhiteLabels();
      onClose();
    } catch (error) {
      console.error("Error rejecting WhiteLabel:", error);
      toast({
        title: "Erro ao rejeitar WhiteLabel",
        description: "Tente novamente mais tarde.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchPendingWhiteLabels();
  }, [page, pageSize]);

  const formatDateTime = (dateTime: Date | undefined) => {
    if (!dateTime) return "N/A";
    const date = new Date(dateTime);
    return `${date.toLocaleDateString("pt-BR")} ${date.toLocaleTimeString(
      "pt-BR",
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    )}`;
  };

  return (
    <Box
      w="100%"
      display="flex"
      flexDirection="column"
      padding={{ base: "2", md: "4" }}
    >
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        color="white"
        boxShadow="md"
        w="100%"
      >
        <Flex
          flexDirection={{ base: "column", md: "row" }}
          alignItems={{ base: "flex-start", md: "center" }}
          justifyContent="space-between"
          mb={4}
        >
          <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
            <MdColorLens size="2rem" />
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              ml={4}
              fontFamily="Lexend Deca"
            >
              WhiteLabel Pendentes
            </Text>
          </Flex>
        </Flex>

        <Flex
          mt={{ base: 4, md: 0 }}
          justifyContent={{ base: "flex-start", md: "flex-start" }}
        >
          <InputGroup width={{ base: "18%", md: "30%" }}>
            <InputLeftElement mt={5} pointerEvents="none">
              <CiSearch color="black" size="2rem" />
            </InputLeftElement>
            <Input
              mt={4}
              placeholder="Pesquisar..."
              variant="filled"
              height={{ base: "56px", md: "48px" }}
              type="text"
              bg={"white"}
              maxLength={30}
              value={search}
              onChange={handleSearch}
              color="black"
              _focus={{ bg: "white", borderColor: "gray.300" }}
              _hover={{ bg: "white" }}
              fontFamily="Lexend Deca"
            />
          </InputGroup>
        </Flex>
      </Box>
      <Flex
        justify="center"
        alignItems="center"
        flexDirection={{ base: "column", md: "row" }}
        width="100%"
        maxWidth="1200px"
        px={{ base: 2, md: 0 }}
        mt={4}
      >
        <Flex alignItems="center" mt={4} mb={{ base: 2, md: 2 }}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            isDisabled={page === 1}
            mr={2}
            aria-label="Previous Page"
            size={{ base: "md", md: "sm" }}
          />
          <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
            Página {page} de {totalPages}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            isDisabled={page === totalPages}
            ml={2}
            aria-label="Next Page"
            size={{ base: "md", md: "sm" }}
          />
        </Flex>
        <Box ml={{ base: 0, md: 4 }} mb={2} mt={{ base: 2, md: 4 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            width={{ base: "100%", md: "auto" }}
            size={{ base: "md", md: "sm" }}
            fontSize={{ base: "lg", md: "md" }}
            fontFamily="Lexend Deca"
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </Select>
        </Box>
      </Flex>

      <Box mt={4}>
        {isLoading ? (
          <Flex justifyContent="center" alignItems="center" height="200px">
            <Spinner size="xl" />
          </Flex>
        ) : filteredWhiteLabels.length > 0 ? (
          <TableContainer borderRadius="md" boxShadow="md" width="100%">
            <Table variant="simple">
              <Thead
                {...backgroundStyle}
                position="relative"
                top={0}
                zIndex={1}
                boxShadow="md"
              >
                <Tr>
                  <Th color="white" textAlign="center" fontSize="xs" py={5}>
                    Nome
                  </Th>
                  <Th color="white" textAlign="center" fontSize="xs" py={5}>
                    <HStack justify="center">
                      <Text>Cor Primária</Text>
                    </HStack>
                  </Th>
                  <Th color="white" textAlign="center" fontSize="xs" py={5}>
                    <HStack justify="center">
                      <Text>Cor Secundária</Text>
                    </HStack>
                  </Th>
                  <Th color="white" textAlign="center" fontSize="xs" py={5}>
                    <HStack justify="center">
                      <Text>Data e Hora de Criação</Text>
                    </HStack>
                  </Th>
                  <Th color="white" textAlign="center" fontSize="xs" py={5}>
                    <HStack justify="center">
                      <Text>Ações</Text>
                    </HStack>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredWhiteLabels.map((whiteLabel) => (
                  <Tr key={whiteLabel.id}>
                    <Td fontFamily="Lexend Deca" textAlign="center">
                      {whiteLabel.name}
                    </Td>
                    <Td textAlign="center">
                      <Box
                        w="20px"
                        h="20px"
                        bg={whiteLabel.primaryColor}
                        border="1px solid black"
                        mx="auto"
                      />
                    </Td>
                    <Td textAlign="center">
                      <Box
                        w="20px"
                        h="20px"
                        bg={whiteLabel.secondaryColor}
                        border="1px solid black"
                        mx="auto"
                      />
                    </Td>
                    <Td fontFamily="Lexend Deca" textAlign="center">
                      {formatDateTime(whiteLabel.createdAt)}
                    </Td>
                    <Td textAlign="center">
                      <Flex justifyContent="center" gap={4}>
                        <Button
                          fontFamily="Lexend Deca"
                          colorScheme="primary"
                          onClick={() => {
                            setSelectedWhiteLabel(whiteLabel);
                            onOpen();
                          }}
                        >
                          Ver Detalhes
                        </Button>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        ) : (
          <Text
            fontFamily="Lexend Deca"
            color="gray.500"
            textAlign="center"
            mt={8}
          >
            Não há WhiteLabels pendentes no momento.
          </Text>
        )}
      </Box>

      {selectedWhiteLabel && (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontFamily="Lexend Deca">
              Detalhes do WhiteLabel
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <WhiteLabelForm
                initialData={selectedWhiteLabel}
                isNew={false}
                onSubmit={() => fetchPendingWhiteLabels()}
              />
            </ModalBody>
            <ModalFooter>
              <Flex w="100%" justifyContent="space-between" alignItems="center">
                <Button
                  fontFamily="Lexend Deca"
                  colorScheme="red"
                  onClick={() => handleReject(selectedWhiteLabel.id)}
                >
                  Rejeitar
                </Button>
                <Button
                  fontFamily="Lexend Deca"
                  colorScheme="primary"
                  onClick={() => handleApprove(selectedWhiteLabel.id)}
                >
                  Aprovar
                </Button>
              </Flex>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      <Flex
        justify="center"
        alignItems="center"
        flexDirection={{ base: "column", md: "row" }}
        width="100%"
        maxWidth="1200px"
        px={{ base: 2, md: 0 }}
        mt={4}
      >
        <Flex alignItems="center" mt={4} mb={{ base: 2, md: 2 }}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            isDisabled={page === 1}
            mr={2}
            aria-label="Previous Page"
            size={{ base: "md", md: "sm" }}
          />
          <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
            Página {page} de {totalPages}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            isDisabled={page === totalPages}
            ml={2}
            aria-label="Next Page"
            size={{ base: "md", md: "sm" }}
          />
        </Flex>
        <Box ml={{ base: 0, md: 4 }} mb={2} mt={{ base: 2, md: 4 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            width={{ base: "100%", md: "auto" }}
            size={{ base: "md", md: "sm" }}
            fontSize={{ base: "lg", md: "md" }}
            fontFamily="Lexend Deca"
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </Select>
        </Box>
      </Flex>
    </Box>
  );
}
